

import { useState } from 'react';

interface FooterProps {
  onOpenModal?: () => void;
}

export default function Footer({ onOpenModal }: FooterProps = {}) {
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsOfUse, setShowTermsOfUse] = useState(false);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openWhatsApp = () => {
    const phoneNumber = "551123746167";
    const message = "Olá! Vim pelo site da M4 Digital e gostaria de mais informações sobre os serviços.";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const openPrivacyPolicy = () => {
    setShowPrivacyPolicy(true);
  };

  const closePrivacyPolicy = () => {
    setShowPrivacyPolicy(false);
  };

  const openTermsOfUse = () => {
    setShowTermsOfUse(true);
  };

  const closeTermsOfUse = () => {
    setShowTermsOfUse(false);
  };

  return (
    <>
      <footer className="bg-gray-900 text-white">
        {/* Main Footer */}
        <div className="container py-16">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <img 
                  src="https://static.readdy.ai/image/60d079db6e314477a5615b4d3efd8435/1aa70460cb4235142615a402493549d1.png" 
                  alt="M4 Digital" 
                  className="h-16 w-auto mb-4 brightness-0 invert"
                />
                <p className="text-gray-300 leading-relaxed max-w-md">
                  Aceleradora de vendas especializada em transformar negócios através da inteligência artificial, automação e estratégias de alto impacto.
                </p>
              </div>
              
              {/* Contact Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-white text-sm"></i>
                  </div>
                  <button 
                    onClick={openWhatsApp}
                    className="text-gray-300 hover:text-orange-400 transition-colors cursor-pointer"
                  >
                    (11) 2374-6167
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                    <i className="ri-map-pin-line text-white text-sm"></i>
                  </div>
                  <span className="text-gray-300">São Bernardo do Campo, SP - Brasil</span>
                </div>
              </div>
              
              {/* Social Media */}
              <div className="flex items-center gap-4">
                <a 
                  href="https://www.linkedin.com/company/m4digita/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gradient-to-r hover:from-orange-500 hover:to-red-500 transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-linkedin-fill"></i>
                </a>
                <a 
                  href="https://www.instagram.com/m4digital.co/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gradient-to-r hover:from-orange-500 hover:to-red-500 transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-instagram-line"></i>
                </a>
                <button 
                  onClick={openWhatsApp}
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gradient-to-r hover:from-orange-500 hover:to-red-500 transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-whatsapp-line"></i>
                </button>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gradient-to-r hover:from-orange-500 hover:to-red-500 transition-all duration-300 cursor-pointer">
                  <i className="ri-telegram-line"></i>
                </a>
              </div>
            </div>
            
            {/* Quick Links */}
            <div>
              <h3 className="text-xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400">
                Links Rápidos
              </h3>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => scrollToSection('hero')}
                    className="text-gray-300 hover:text-orange-400 transition-colors cursor-pointer"
                  >
                    Início
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('sobre')}
                    className="text-gray-300 hover:text-orange-400 transition-colors cursor-pointer"
                  >
                    Sobre Nós
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('solucoes')}
                    className="text-gray-300 hover:text-orange-400 transition-colors cursor-pointer"
                  >
                    Soluções
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('resultados')}
                    className="text-gray-300 hover:text-orange-400 transition-colors cursor-pointer"
                  >
                    Cases de Sucesso
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('faq')}
                    className="text-gray-300 hover:text-orange-400 transition-colors cursor-pointer"
                  >
                    FAQ
                  </button>
                </li>
              </ul>
            </div>
            
            {/* Services */}
            <div>
              <h3 className="text-xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400">
                Nossos Serviços
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
                  <span className="text-gray-300">Tráfego Pago</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
                  <span className="text-gray-300">CRM & Automação</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
                  <span className="text-gray-300">Agentes de IA</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
                  <span className="text-gray-300">WhatsApp API</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
                  <span className="text-gray-300">Lançamentos</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
                  <span className="text-gray-300">Consultoria</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
                  <span className="text-gray-300">Reativação de Leads</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
                  <span className="text-gray-300">Treinamentos</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* CTA Section */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 py-12">
          <div className="container text-center">
            <h3 className="text-3xl font-bold mb-4">
              Pronto para Acelerar suas Vendas?
            </h3>
            <p className="text-xl mb-8 opacity-90">
              Agende uma consultoria gratuita e transforme seus resultados
            </p>
            <div className="flex justify-center">
              <button 
                onClick={onOpenModal || (() => scrollToSection('diagnostico'))}
                className="bg-white text-orange-600 px-10 py-5 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
              >
                <i className="ri-calendar-line mr-2"></i>
                AGENDAR REUNIÃO
              </button>
            </div>
          </div>
        </div>
        
        {/* Bottom Footer */}
        <div className="bg-gray-800 py-6">
          <div className="container">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-gray-400 text-sm">
                © 2025 M4 Digital. Todos os direitos reservados.
              </div>
              <div className="flex items-center gap-6 text-sm">
                <button 
                  onClick={openPrivacyPolicy}
                  className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer"
                >
                  Política de Privacidade
                </button>
                <button 
                  onClick={openTermsOfUse}
                  className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer"
                >
                  Termos de Uso
                </button>
                <a href="/" className="text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">
                  Desenvolvido por M4 Digital
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Popup */}
      {showPrivacyPolicy && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                <i className="ri-shield-check-line text-orange-500 mr-3"></i>
                Política de Privacidade – M4 Digital
              </h2>
              <button 
                onClick={closePrivacyPolicy}
                className="text-gray-400 hover:text-gray-600 text-2xl cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 mb-6 leading-relaxed">
                  A M4 Digital valoriza a sua privacidade e está comprometida em proteger os dados pessoais de todos os usuários, clientes e visitantes que interagem com nossos sites, sistemas e serviços.
                  Esta Política de Privacidade explica como coletamos, utilizamos, armazenamos e protegemos suas informações, em conformidade com a Lei Geral de Proteção de Dados (LGPD – Lei nº 13.709/2018).
                </p>

                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">1</span>
                      Coleta de Informações
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Coletamos informações pessoais apenas quando necessário para fornecer nossos serviços de forma eficiente e segura.
                      As informações podem ser coletadas de forma direta (quando você as fornece) ou automática (através de cookies e ferramentas analíticas).
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-800 mb-3">Tipos de dados coletados:</h4>
                      <ul className="space-y-2 text-gray-600">
                        <li className="flex items-center"><i className="ri-check-line text-orange-500 mr-2"></i>Nome completo</li>
                        <li className="flex items-center"><i className="ri-check-line text-orange-500 mr-2"></i>E-mail e número de telefone</li>
                        <li className="flex items-center"><i className="ri-check-line text-orange-500 mr-2"></i>Empresa e cargo (quando aplicável)</li>
                        <li className="flex items-center"><i className="ri-check-line text-orange-500 mr-2"></i>Informações fornecidas voluntariamente em formulários, cadastros ou conversas via WhatsApp e CRM</li>
                        <li className="flex items-center"><i className="ri-check-line text-orange-500 mr-2"></i>Dados de navegação (IP, localização aproximada, tipo de dispositivo e tempo de acesso)</li>
                      </ul>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">2</span>
                      Uso das Informações
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Os dados coletados são utilizados com finalidades específicas, legítimas e transparentes, tais como:
                    </p>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start"><i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>Entrar em contato com você após seu cadastro em formulários, campanhas ou landing pages</li>
                      <li className="flex items-start"><i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>Enviar comunicações sobre produtos, serviços e conteúdos relevantes da M4 Digital</li>
                      <li className="flex items-start"><i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>Personalizar sua experiência em nosso site e melhorar nossos serviços</li>
                      <li className="flex items-start"><i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>Cumprir obrigações legais e regulatórias</li>
                      <li className="flex items-start"><i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>Analisar dados de performance e comportamento para otimização de campanhas com Inteligência de Dados e IA</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">3</span>
                      Armazenamento e Proteção dos Dados
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Os dados são armazenados em ambientes seguros e controlados, com uso de criptografia, autenticação e protocolos de segurança.
                      A M4 Digital adota práticas avançadas de proteção da informação, impedindo o acesso, uso, modificação ou divulgação não autorizada de seus dados.
                    </p>
                    <p className="text-gray-600">
                      O período de retenção dos dados respeita a finalidade de uso, sendo excluídos ou anonimizados quando não forem mais necessários.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">4</span>
                      Compartilhamento de Dados
                    </h3>
                    <p className="text-gray-600 mb-4">
                      A M4 Digital não vende nem compartilha dados pessoais com terceiros sem consentimento.
                      Os dados podem ser compartilhados apenas com:
                    </p>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start"><i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>Parceiros de tecnologia oficiais (ex.: Meta, Google, Lovoo, etc.), para fins de integração e automação de processos</li>
                      <li className="flex items-start"><i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>Fornecedores e colaboradores que auxiliam na execução de serviços, sempre sob acordo de confidencialidade</li>
                      <li className="flex items-start"><i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>Autoridades legais, quando houver obrigação jurídica</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">5</span>
                      Cookies e Tecnologias de Rastreamento
                    </h3>
                    <p className="text-gray-600">
                      Utilizamos cookies para melhorar a experiência de navegação, entender o comportamento dos visitantes e otimizar campanhas.
                      Você pode configurar seu navegador para recusar cookies, mas isso pode limitar algumas funcionalidades do site.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">6</span>
                      Seus Direitos
                    </h3>
                    <p className="text-gray-600 mb-4">
                      De acordo com a LGPD, você tem direito a:
                    </p>
                    <ul className="space-y-2 text-gray-600 mb-4">
                      <li className="flex items-start"><i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>Acessar, corrigir ou excluir seus dados pessoais</li>
                      <li className="flex items-start"><i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>Revogar o consentimento de uso de dados a qualquer momento</li>
                      <li className="flex items-start"><i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>Solicitar informações sobre o tratamento de seus dados</li>
                    </ul>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <p className="text-gray-700">
                        Para exercer seus direitos, entre em contato pelo e-mail:
                        <br />
                        <strong className="text-orange-600">📩 contato@m4digital.co</strong>
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">7</span>
                      Alterações na Política de Privacidade
                    </h3>
                    <p className="text-gray-600">
                      A M4 Digital pode atualizar esta Política de Privacidade periodicamente.
                      Recomendamos que você revise este documento regularmente para se manter informado sobre como protegemos suas informações.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">8</span>
                      Contato
                    </h3>
                    <p className="text-gray-600">
                      Em caso de dúvidas sobre esta Política ou sobre o tratamento de seus dados pessoais, entre em contato conosco.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6">
              <button 
                onClick={closePrivacyPolicy}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 cursor-pointer"
              >
                <i className="ri-check-line mr-2"></i>
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terms of Use Popup */}
      {showTermsOfUse && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                <i className="ri-file-text-line text-orange-500 mr-3"></i>
                Termos de Uso – M4 Digital
              </h2>
              <button 
                onClick={closeTermsOfUse}
                className="text-gray-400 hover:text-gray-600 text-2xl cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Bem-vindo ao site da M4 Digital.
                  Ao acessar e utilizar nossos sites, sistemas, serviços e conteúdos, você concorda com os termos e condições abaixo. Recomendamos a leitura atenta deste documento antes de continuar a navegação.
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Estes Termos regem o uso do site m4.digital e demais plataformas, pertencentes à M4 Digital – Aceleradora de Vendas.
                </p>

                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">1</span>
                      Aceitação dos Termos
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Ao acessar, navegar ou utilizar qualquer funcionalidade do site ou dos serviços da M4 Digital, o usuário reconhece ter lido, compreendido e concordado integralmente com este Termo de Uso e com a Política de Privacidade.
                    </p>
                    <p className="text-gray-600">
                      Caso não concorde com qualquer condição, recomendamos não utilizar os serviços e interromper o acesso imediatamente.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">2</span>
                      Descrição dos Serviços
                    </h3>
                    <p className="text-gray-600 mb-4">
                      A M4 Digital é uma aceleradora de vendas especializada em tráfego pago, automação comercial, implementação de CRM, inteligência artificial e estratégias de marketing digital.
                    </p>
                    <p className="text-gray-600">
                      Por meio do site, oferecemos informações sobre nossos serviços, materiais educativos, campanhas promocionais e canais de contato para captação de leads e atendimento comercial.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">3</span>
                      Uso do Site e Responsabilidades do Usuário
                    </h3>
                    <p className="text-gray-600 mb-4">
                      O usuário se compromete a utilizar o site e os serviços da M4 Digital de forma ética, responsável e em conformidade com a legislação vigente.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold text-gray-800 mb-3">É expressamente proibido:</h4>
                      <ul className="space-y-2 text-gray-600">
                        <li className="flex items-start"><i className="ri-close-circle-line text-red-500 mr-2 mt-1"></i>Utilizar o site para fins ilegais, difamatórios ou que prejudiquem terceiros</li>
                        <li className="flex items-start"><i className="ri-close-circle-line text-red-500 mr-2 mt-1"></i>Tentar invadir sistemas, modificar códigos ou interferir na estrutura operacional da M4 Digital</li>
                        <li className="flex items-start"><i className="ri-close-circle-line text-red-500 mr-2 mt-1"></i>Copiar, reproduzir ou distribuir qualquer conteúdo do site sem autorização prévia por escrito</li>
                        <li className="flex items-start"><i className="ri-close-circle-line text-red-500 mr-2 mt-1"></i>Fornecer informações falsas, incorretas ou desatualizadas em formulários e cadastros</li>
                      </ul>
                    </div>
                    <p className="text-gray-600">
                      O descumprimento dessas condições poderá resultar na suspensão ou bloqueio de acesso, sem prejuízo das medidas legais cabíveis.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">4</span>
                      Propriedade Intelectual
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Todo o conteúdo do site — incluindo textos, imagens, logotipos, vídeos, ícones, gráficos, layouts, softwares e marcas — é de propriedade exclusiva da M4 Digital e protegido pelas leis de direitos autorais e propriedade intelectual.
                    </p>
                    <p className="text-gray-600">
                      Nenhum material poderá ser copiado, reproduzido, transmitido, exibido ou distribuído sem autorização expressa da M4 Digital.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">5</span>
                      Tratamento de Dados Pessoais
                    </h3>
                    <p className="text-gray-600 mb-4">
                      O uso do site implica na concordância com a Política de Privacidade da M4 Digital, que regula a coleta, uso, armazenamento e proteção dos dados pessoais em conformidade com a Lei Geral de Proteção de Dados (LGPD – Lei nº 13.709/2018).
                    </p>
                    <p className="text-gray-600">
                      O usuário poderá, a qualquer momento, solicitar acesso, correção ou exclusão de seus dados, conforme previsto na Política de Privacidade.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">6</span>
                      Isenção de Responsabilidade
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold text-gray-800 mb-3">A M4 Digital não se responsabiliza por:</h4>
                      <ul className="space-y-2 text-gray-600">
                        <li className="flex items-start"><i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>Danos causados por falhas de conexão, vírus ou interrupções temporárias do site</li>
                        <li className="flex items-start"><i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>Conteúdos ou links externos que direcionem o usuário a outros sites</li>
                        <li className="flex items-start"><i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>Informações fornecidas incorretamente por terceiros</li>
                        <li className="flex items-start"><i className="ri-arrow-right-s-line text-orange-500 mr-2 mt-1"></i>Uso indevido das informações disponibilizadas no site</li>
                      </ul>
                    </div>
                    <p className="text-gray-600">
                      O conteúdo publicado tem caráter informativo e institucional, não configurando promessa de resultado financeiro específico.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">7</span>
                      Atualizações e Modificações dos Termos
                    </h3>
                    <p className="text-gray-600 mb-4">
                      A M4 Digital poderá alterar, atualizar ou revisar este Termo de Uso a qualquer momento, sem aviso prévio, para adequação legal ou melhoria dos serviços.
                      Recomendamos a leitura periódica deste documento, disponível permanentemente em www.m4.digital/termos.
                    </p>
                    <p className="text-gray-600">
                      O uso continuado do site após alterações constitui aceitação integral dos novos Termos.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">8</span>
                      Legislação e Foro Competente
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Este Termo é regido pelas leis da República Federativa do Brasil.
                    </p>
                    <p className="text-gray-600">
                      Fica eleito o foro da Comarca de São Bernardo do Campo - SP, com renúncia a qualquer outro, para dirimir controvérsias decorrentes deste documento.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">9</span>
                      Contato
                    </h3>
                    <p className="text-gray-600">
                      Para dúvidas, solicitações ou esclarecimentos sobre este Termo, entre em contato conosco.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6">
              <button 
                onClick={closeTermsOfUse}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 cursor-pointer"
              >
                <i className="ri-check-line mr-2"></i>
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

